import React from 'react';
import { Partido, getCategoriaLabel, getCompeticionLabel } from '@/types';
import { Card } from './Card';
import { ExternalLink, MapPin } from 'lucide-react';
import { abrirEnMapas, hayUbicacion } from '@/utils/abrirEnMapas';

interface PartidoCardProps {
  partido: Partido;
  onClick?: () => void;
}

/**
 * Componente para mostrar una tarjeta de partido
 * @param partido - Datos del partido
 * @param onClick - Función a ejecutar al hacer clic
 */
export const PartidoCard: React.FC<PartidoCardProps> = ({ partido, onClick }) => {
  /**
   * Formatea la fecha y hora del partido
   */
  const formatearFechaHora = (fechaHora: string): string => {
    const fecha = new Date(fechaHora);
    const opciones: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    };
    return fecha.toLocaleDateString('es-ES', opciones);
  };

  const irAlCampo = (e: React.MouseEvent) => {
    e.stopPropagation();   // el clic de la tarjeta abre el detalle: aquí no queremos eso
    abrirEnMapas(partido.latitud, partido.longitud, partido.campo);
  };

  return (
    <Card 
      variant="glass"
      className={`p-4 cursor-pointer hover:border-emerald-500/50 hover:bg-slate-900/40 transition-all duration-300 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)]`}
      onClick={onClick}
    >
      <div className="flex justify-between items-start">
        {/* Lado izquierdo - Información principal */}
        <div className="flex-1 pr-4">
          <h3 className="text-lg font-semibold text-slate-100 mb-2">
            {partido.nombre}
          </h3>
          
          <div className="flex items-center text-slate-400 text-sm">
            <svg 
              className="w-4 h-4 mr-2 text-emerald-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span>{formatearFechaHora(partido.fechaHora)}</span>
          </div>

          {/* Campo de juego: pulsable si tenemos coordenadas */}
          {partido.campo && (
            hayUbicacion(partido.latitud, partido.longitud) ? (
              <button
                type="button"
                onClick={irAlCampo}
                className="mt-2 flex items-center gap-1.5 text-left text-emerald-400 transition-opacity active:opacity-70"
              >
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs leading-tight line-clamp-2 underline decoration-emerald-400/40 underline-offset-2">
                  {partido.campo}
                </span>
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </button>
            ) : (
              <div className="mt-2 flex items-center gap-1.5 text-slate-500">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs leading-tight line-clamp-2">{partido.campo}</span>
              </div>
            )
          )}
        </div>
        
        {/* Lado derecho - Competición, categoría y grupo */}
        <div className="text-right">
          <div className="text-emerald-400 font-bold text-sm mb-1">
            {getCompeticionLabel(partido.competicion)}
          </div>
          <div className="text-slate-300 text-sm mb-1">
            {getCategoriaLabel(partido.categoria)}
          </div>
          <div className="text-slate-400 text-sm">
            G{partido.grupo}
          </div>
        </div>
      </div>
    </Card>
  );
};